import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AutoLogoutPopup from './AutoLogoutMessage';
import { routes } from '../../routes/Routes';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { invalidateSession, logoutUser } from '../../store/slice/sessionSlice';

interface SessionTimeoutProps {
  timeoutInMinutes: number;
  warningInMinutes: number;
  children: React.ReactNode;
}

const SessionTimeout: React.FC<SessionTimeoutProps> = ({
  timeoutInMinutes,
  warningInMinutes,
  children
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data } = useAppSelector((state) => state.session);
  const { isLoggedIn, isSessionValid } = data;

  const warningTimeout = useRef<NodeJS.Timeout | null>(null);
  const logoutTimeout = useRef<NodeJS.Timeout | null>(null);

  const [showWarning, setShowWarning] = useState(false);
  const [showAutoLogout, setShowAutoLogout] = useState(false);

  // Clears all timers
  const clearTimers = () => {
    if (warningTimeout.current) clearTimeout(warningTimeout.current);
    if (logoutTimeout.current) clearTimeout(logoutTimeout.current);
  };

  // Resets the warning and logout timers
  const resetTimers = useCallback(() => {
    clearTimers();

    const timeoutMs = timeoutInMinutes * 60 * 1000;
    const warningMs = warningInMinutes * 60 * 1000;

    // Show warning popup warningInMinutes before session expires
    warningTimeout.current = setTimeout(() => {
      setShowWarning(true);
    }, timeoutMs - warningMs);

    // Logout after timeoutInMinutes
    logoutTimeout.current = setTimeout(() => {
      setShowWarning(false);
      setShowAutoLogout(true);
      dispatch(invalidateSession());
    }, timeoutMs);
  }, [timeoutInMinutes, warningInMinutes, dispatch]);

  // User clicks "No" - keep session alive
  const handleResumeSession = () => {
    setShowWarning(false);
    resetTimers();
  };

  // User clicks "Yes" - logout now
  const handleLogoutNow = () => {
    setShowWarning(false);
    setShowAutoLogout(true);
    dispatch(invalidateSession());
  };

  // User confirms auto logout popup - redirect to login
  const handleAutoLogoutConfirm = () => {
    dispatch(logoutUser());
    void navigate(routes.login);
  };

  // Setup event listeners to reset timers on user activity
  useEffect(() => {
    if (!isLoggedIn || !isSessionValid) {
      // If logged out or session invalid, clear timers and exit
      clearTimers();
      return;
    }

    resetTimers();

    const events = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

    const resetOnActivity = () => {
      resetTimers();
    };

    events.forEach((event) => document.addEventListener(event, resetOnActivity));

    return () => {
      clearTimers();
      events.forEach((event) => document.removeEventListener(event, resetOnActivity));
    };
  }, [isLoggedIn, isSessionValid, resetTimers]);

  // Show AutoLogoutPopup after session invalidation
  if (showAutoLogout || !isSessionValid || showWarning) {
    return (
      <AutoLogoutPopup
        onConfirm={handleAutoLogoutConfirm}
        onCancel={handleResumeSession}
        isSessionExpired={!isSessionValid}
      />
    );
  }

  return <>{children}</>;
};

export default SessionTimeout;
